// "Remember on this phone for 30 days" (Mike, 2026-10-20; replaced the 12-hour remember of 2026-10-08,
// which kept the raw Admin PIN in localStorage, unticked by default, and never checked back with JobQ).
//
// Why anything is kept: an admin unlock lives in sessionStorage (adminVerified, userId, userName, userRole,
// adminPin), which iPhone clears whenever the home-screen app is closed, so the admin pages asked for the PIN
// on every launch. The session keeps the PIN itself because the app autofills it into action PIN prompts and
// sends it as X-Admin-Pin to /security (utils/securityApi.js).
//
// How it is kept (jobq-api "Remembered admin phones"): ticking Remember sends the PIN to
// POST /admin_devices once; JobQ answers a device token and an AES key. The phone encrypts the PIN with that
// key (AES-GCM, WebCrypto), stores {token, iv, ciphertext} in localStorage and throws the key away. On each
// app start POST /admin_devices/unlock {token} asks JobQ for the key again: JobQ says no once the 30 days are
// up, the phone was signed out (Clear Credentials here, or Sign out in User Management), the person is no
// longer an active Admin, or their PIN changed. A no deletes the record; no answer at all (offline, JobQ down)
// keeps it and tries again when the app comes back. The phone's storage alone holds no PIN anyone can read.
//
// Every reader of the unlock still looks in sessionStorage: unlockRememberedAdmin() puts the PIN back there.
// The API base is passed in (config.js is not imported here: it wraps window.fetch at import).

export const ADMIN_REMEMBER_KEY = 'jobq.adminRemember';
export const ADMIN_REMEMBER_DAYS = 30;
export const ADMIN_REMEMBER_MS = ADMIN_REMEMBER_DAYS * 24 * 60 * 60 * 1000;
const CLOCK_SLACK_MS = 24 * 60 * 60 * 1000; // JobQ's clock vs this phone's
const LEGACY_MAX_MS = 12 * 60 * 60 * 1000; // a 12-hour record from before 2026-10-20

const SESSION_KEYS = ['adminVerified', 'userId', 'userName', 'userRole', 'adminPin'];

function local() {
  try { return window.localStorage; } catch (e) { return null; }
}
function session() {
  try { return window.sessionStorage; } catch (e) { return null; }
}
function webCrypto() {
  const c = typeof window !== 'undefined' ? window.crypto : null;
  return c && c.subtle && c.getRandomValues ? c : null;
}

/** Can this browser keep an encrypted PIN at all (WebCrypto needs https)? */
export function canRememberAdmin() {
  return Boolean(webCrypto() && local());
}

// ── storage ──

function readRaw() {
  const ls = local();
  if (!ls) return null;
  let raw = null;
  try { raw = ls.getItem(ADMIN_REMEMBER_KEY); } catch (e) { return null; }
  if (!raw) return null;
  try { return JSON.parse(raw); } catch (e) { return { broken: true }; }
}

function writeRecord(rec) {
  const ls = local();
  if (!ls) return false;
  try {
    ls.setItem(ADMIN_REMEMBER_KEY, JSON.stringify(rec));
    return true;
  } catch (e) {
    return false;
  }
}

function dropRecord() {
  const ls = local();
  try { if (ls) ls.removeItem(ADMIN_REMEMBER_KEY); } catch (e) { /* nothing to drop */ }
}

const hasId = (v) => v !== undefined && v !== null && v !== '';

/**
 * The live remembered unlock on this phone, or null; anything expired, broken or implausible is deleted.
 * v: 2 = the JobQ-held key ({deviceId, token, iv, ct, userId, userName, expiresAt}); v: 1 = a 12-hour record
 * from before 2026-10-20 (raw PIN), read only so it can be moved over once.
 */
export function readRememberedAdmin(now = Date.now()) {
  const rec = readRaw();
  if (!rec) return null;
  let valid = false;
  if (rec && rec.v === 2) {
    valid = typeof rec.token === 'string' && rec.token.length >= 20
      && typeof rec.iv === 'string' && rec.iv && typeof rec.ct === 'string' && rec.ct
      && hasId(rec.userId)
      && typeof rec.expiresAt === 'number' && rec.expiresAt > now
      && rec.expiresAt - now <= ADMIN_REMEMBER_MS + CLOCK_SLACK_MS;
  } else if (rec && !rec.broken && rec.v === undefined) {
    valid = rec.role === 'admin' && hasId(rec.userId) && typeof rec.pin === 'string' && rec.pin
      && typeof rec.expiresAt === 'number' && rec.expiresAt > now && rec.expiresAt - now <= LEGACY_MAX_MS;
    if (valid) return { ...rec, v: 1 };
  }
  if (!valid) {
    dropRecord();
    return null;
  }
  return rec;
}

/** Is there a remembered unlock to try? */
export function hasRememberedAdmin(now = Date.now()) {
  return readRememberedAdmin(now) !== null;
}

/** When the remembered unlock ends (ms since epoch), or null when there is none. */
export function rememberedAdminExpiresAt(now = Date.now()) {
  const rec = readRememberedAdmin(now);
  return rec ? rec.expiresAt : null;
}

// ── sessionStorage (where every reader looks) ──

export function setAdminSessionKeys(user) {
  const ss = session();
  if (!ss || !user) return;
  try {
    ss.setItem('adminVerified', 'true');
    ss.setItem('userId', String(user.userId));
    ss.setItem('userName', user.userName || '');
    ss.setItem('userRole', String(user.role || '').toLowerCase());
    if (user.pin) ss.setItem('adminPin', user.pin);
  } catch (e) { /* storage blocked: the PIN is asked as before */ }
}

export function clearAdminSessionKeys() {
  const ss = session();
  if (!ss) return;
  SESSION_KEYS.forEach((k) => { try { ss.removeItem(k); } catch (e) { /* ignore */ } });
}

// ── the API ──

export const UNLOCK_TIMEOUT_MS = 8000; // an app start never waits longer than this for JobQ's answer
const REMEMBER_TIMEOUT_MS = 25000; // POST /admin_devices checks the PIN (bcrypt): slower

/** JobQ's own "no" to an unlock (jobq-api admin_devices.REFUSAL_CODES): the record is deleted. */
export const UNLOCK_REFUSALS = Object.freeze(['unknown', 'signed_out', 'expired', 'inactive', 'not_admin', 'pin_changed']);

async function post(apiBase, path, body, timeoutMs = 0) {
  const ctrl = timeoutMs && typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  try {
    const res = await fetch(`${apiBase}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      ...(ctrl ? { signal: ctrl.signal } : {}),
    });
    let data = null;
    try { data = await res.json(); } catch (e) { data = null; }
    return { ok: res.ok, status: res.status, body: data };
  } catch (e) {
    return { ok: false, status: 0, body: null }; // no answer (offline, aborted after the timeout)
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** The API's refusal code ({detail: {code}} or {code}), else null. */
export function refusalCode(body) {
  if (!body || typeof body !== 'object') return null;
  if (body.detail && typeof body.detail === 'object' && body.detail.code) return String(body.detail.code);
  return body.code ? String(body.code) : null;
}

/** The API's sentence for a refusal ({detail: {message}}), else null. */
export function refusalMessage(body) {
  if (!body || typeof body !== 'object') return null;
  if (body.detail && typeof body.detail === 'object' && body.detail.message) return String(body.detail.message);
  return null;
}

// ── the PIN, encrypted with the key JobQ holds ──

const bytesToB64 = (bytes) => {
  let s = '';
  bytes.forEach((b) => { s += String.fromCharCode(b); });
  return btoa(s);
};
const b64ToBytes = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
const aad = (deviceId) => new TextEncoder().encode(`jobq-admin-pin:v1:${deviceId}`);

async function aesKey(c, keyB64) {
  return c.subtle.importKey('raw', b64ToBytes(keyB64), { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

/** {iv, ct} (base64) of the PIN under JobQ's key, bound to this device id. */
export async function encryptPin(keyB64, pin, deviceId) {
  const c = webCrypto();
  const iv = c.getRandomValues(new Uint8Array(12));
  const ct = await c.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: aad(deviceId) },
    await aesKey(c, keyB64), new TextEncoder().encode(pin));
  return { iv: bytesToB64(iv), ct: bytesToB64(new Uint8Array(ct)) };
}

/** The PIN back, or throws (wrong key, tampered record). */
export async function decryptPin(keyB64, ivB64, ctB64, deviceId) {
  const c = webCrypto();
  const plain = await c.subtle.decrypt({ name: 'AES-GCM', iv: b64ToBytes(ivB64), additionalData: aad(deviceId) },
    await aesKey(c, keyB64), b64ToBytes(ctB64));
  return new TextDecoder().decode(plain);
}

/** "iPhone · home-screen app", "Android phone · browser": how the phone shows in User Management. */
export function deviceLabel() {
  let ua = '';
  let standalone = false;
  try {
    ua = navigator.userAgent || '';
    standalone = Boolean((window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
      || navigator.standalone);
  } catch (e) { /* defaults */ }
  const kind = /iPhone/.test(ua) ? 'iPhone' : /iPad/.test(ua) ? 'iPad' : /Android/.test(ua) ? 'Android phone' : 'Phone';
  return `${kind} · ${standalone ? 'home-screen app' : 'browser'}`;
}

/**
 * Remember this phone: POST /admin_devices {pin, label} (JobQ checks the PIN again), encrypt the PIN with the
 * key it answers and keep the record. A phone remembered before is signed out at JobQ (one live record per
 * phone). Resolves {ok: true, expiresAt} or {ok: false, reason, status}. Never throws.
 */
export async function rememberAdminDevice(user, { apiBase, label = deviceLabel(), now = Date.now() } = {}) {
  if (!user || !user.pin) return { ok: false, reason: 'no_pin', status: 0 };
  if (!canRememberAdmin()) return { ok: false, reason: 'no_crypto', status: 0 };
  const before = readRaw();
  const r = await post(apiBase, '/admin_devices', { pin: user.pin, label }, REMEMBER_TIMEOUT_MS);
  if (!r.ok || !r.body || !r.body.token || !r.body.key) {
    return { ok: false, reason: refusalCode(r.body) || (r.status ? 'refused' : 'offline'), status: r.status,
      message: refusalMessage(r.body) };
  }
  const b = r.body;
  try {
    const { iv, ct } = await encryptPin(b.key, user.pin, b.device_id);
    const serverExpiry = Date.parse(b.expires_at);
    const expiresAt = Number.isFinite(serverExpiry) ? Math.min(serverExpiry, now + ADMIN_REMEMBER_MS) : now + ADMIN_REMEMBER_MS;
    const stored = writeRecord({
      v: 2,
      deviceId: b.device_id,
      token: b.token,
      iv,
      ct,
      userId: hasId(b.user_id) ? b.user_id : user.userId,
      userName: b.name || user.userName || '',
      expiresAt,
    });
    if (!stored) {
      post(apiBase, '/admin_devices/sign_out', { token: b.token });
      return { ok: false, reason: 'storage', status: 0 };
    }
    if (before && before.v === 2 && before.token && before.token !== b.token) {
      post(apiBase, '/admin_devices/sign_out', { token: before.token }); // the one it replaces
    }
    return { ok: true, expiresAt };
  } catch (e) {
    post(apiBase, '/admin_devices/sign_out', { token: b.token });
    return { ok: false, reason: 'crypto', status: 0 };
  }
}

/**
 * At start-up (and when the app comes back): turn the remembered record into an unlock.
 * Resolves one of:
 *   {user: {userId, userName, role, pin}, expiresAt}  JobQ said yes (or moved a 12-hour record over)
 *   {locked: true, reason, message}                    nothing remembered, or JobQ said no (record deleted;
 *                                                      message = JobQ's sentence, e.g. the PIN changed)
 *   {offline: true}                                    no answer from JobQ: record kept, try again later
 * Never throws.
 */
export async function unlockRememberedAdmin({ apiBase, now = Date.now() } = {}) {
  const rec = readRememberedAdmin(now);
  if (!rec) return { locked: true, reason: 'none' };

  if (rec.v === 1) {
    // A 12-hour record (raw PIN, before 2026-10-20): move it to the JobQ-held key once.
    const user = { userId: rec.userId, userName: rec.userName, role: 'admin', pin: rec.pin };
    const moved = await rememberAdminDevice(user, { apiBase, now });
    if (moved.ok) return { user, expiresAt: moved.expiresAt };
    if (moved.reason === 'bad_pin') { // JobQ's no: that PIN is no longer an active Admin's
      dropRecord();
      return { locked: true, reason: 'bad_pin', message: moved.message };
    }
    // anything else (no answer, a JobQ without the route yet, not set up, a refused API key): as before,
    // until the 12 hours are up
    return { user, expiresAt: rec.expiresAt };
  }

  const r = await post(apiBase, '/admin_devices/unlock', { token: rec.token }, UNLOCK_TIMEOUT_MS);
  // Only JobQ's own no deletes the record (a 401 with one of its codes). Anything else (no answer, a JobQ
  // without the route, not set up, too many tries, a refused API key) keeps it for the next try.
  const code = refusalCode(r.body);
  if ((r.status === 401 || r.status === 403) && UNLOCK_REFUSALS.includes(code)) {
    dropRecord();
    return { locked: true, reason: code, message: refusalMessage(r.body) };
  }
  if (!r.ok || !r.body || !r.body.key) return { offline: true };
  let pin;
  try {
    pin = await decryptPin(r.body.key, rec.iv, rec.ct, rec.deviceId);
  } catch (e) {
    dropRecord();
    post(apiBase, '/admin_devices/sign_out', { token: rec.token });
    return { locked: true, reason: 'unreadable' };
  }
  const serverExpiry = Date.parse(r.body.expires_at);
  const expiresAt = Number.isFinite(serverExpiry) ? Math.min(serverExpiry, now + ADMIN_REMEMBER_MS) : rec.expiresAt;
  const userName = r.body.name || rec.userName || '';
  // Remembered again while this answer was on its way (a newer token): never put the old one back.
  const current = readRaw();
  if (current && current.v === 2 && current.token === rec.token) writeRecord({ ...current, userName, expiresAt });
  return {
    user: { userId: hasId(r.body.user_id) ? r.body.user_id : rec.userId, userName, role: r.body.role || 'admin', pin },
    expiresAt,
  };
}

/**
 * Forget the remembered unlock on this phone; by default also sign the phone out at JobQ (so the token can
 * never unlock again, even from a copy of this phone's storage). Fire-and-forget: never waits, never throws.
 */
export function forgetRememberedAdmin({ apiBase = null, signOut = true } = {}) {
  const rec = readRaw();
  dropRecord();
  if (signOut && apiBase && rec && rec.v === 2 && rec.token) {
    post(apiBase, '/admin_devices/sign_out', { token: rec.token });
  }
}
